/**
 * @license
 *
 * Copyright IBM Corp. 2020, 2024
 *
 * This source code is licensed under the Apache-2.0 license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { html, LitElement } from 'lit';
import { property } from 'lit/decorators.js';
import FocusMixin from '@carbon/web-components/es/globals/mixins/focus.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import IBM8BarLogoH65White from '../../../es/icons/IBM-8bar-logo--h65-white.js';
import IBM8BarLogoH23White from '../../../es/icons/IBM-8bar-logo--h23-white.js';
import settings from '@carbon/ibmdotcom-utilities/es/utilities/settings/settings.js';
import StableSelectorMixin from '../../globals/mixins/stable-selector';
import { FOOTER_SIZE } from './footer';
import styles from './footer.scss';
import { carbonElement as customElement } from '@carbon/web-components/es/globals/decorators/carbon-element.js';

const { stablePrefix: c4dPrefix } = settings;

/**
 * The IBM logo UI in footer.
 *
 * @element c4d-footer-logo
 */
@customElement(`${c4dPrefix}-footer-logo`)
class C4DFooterLogo extends StableSelectorMixin(FocusMixin(LitElement)) {
  /**
   * Link `href`.
   */
  @property()
  href = 'https://www.ibm.com/';

  /**
   * Size property used for applying classes
   */
  @property()
  size = FOOTER_SIZE.REGULAR;

  /**
   * The shadow slot this logo UI should be in.
   */
  @property({ reflect: true })
  slot = 'brand';

  render() {
    const { href, size } = this;
    return html`
      <a
        part="logo-link"
        class="${c4dPrefix}--footer-logo__link"
        aria-label="IBM logo"
        href="${ifDefined(href)}">
        ${size !== FOOTER_SIZE.MICRO
          ? IBM8BarLogoH65White()
          : IBM8BarLogoH23White()}
        <slot></slot>
      </a>
    `;
  }

  static get stableSelector() {
    return `${c4dPrefix}--footer-logo`;
  }

  static shadowRootOptions = {
    ...LitElement.shadowRootOptions,
    delegatesFocus: true,
  };
  static styles = styles; // `styles` here is a `CSSResult` generated by custom WebPack loader
}

export default C4DFooterLogo;
